import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import CampaignModalPhrase from "../../components/CampaignModalPhrase";
import { AddCircle, TextFields } from "@mui/icons-material";
import { CircularProgress, Grid, Stack, Chip, Box, Tooltip } from "@mui/material";
import { Can } from "../../components/Can";
import {
  colorBackgroundTable,
  colorLineTable,
  colorLineTableHover,
  colorTopTable,
} from "../../styles/styles";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    backgroundColor: colorBackgroundTable(),
    borderRadius: 12,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeader: {
    padding: "8px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    marginBottom: 4,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "30vh",
    gap: 8,
    padding: theme.spacing(4),
  },
  whatsappChip: {
    margin: "2px",
    fontSize: "0.72rem",
    height: 20,
  },
  connectionContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    alignItems: "center",
  },
}));

const CampaignsPhrase = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [campaignflows, setCampaignFlows] = useState([]);
  const [ModalOpenPhrase, setModalOpenPhrase] = useState(false);
  const [campaignflowSelected, setCampaignFlowSelected] = useState();
  const [whatsApps, setWhatsApps] = useState([]);

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/flowcampaign/${campaignId}`);
      toast.success("Campaña eliminada con éxito");
      getCampaigns();
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setDeletingContact(null);
  };

  const getWhatsApps = async () => {
    try {
      const response = await api.get("/whatsapp");
      setWhatsApps(response.data || []);
    } catch (error) {
      console.error("Error al obtener conexiones:", error);
    }
  };

  const getCampaigns = async () => {
    try {
      setLoading(true);
      const response = await api.get("/flowcampaign");
      let flowData = [];
      if (response.data) {
        if (Array.isArray(response.data.flow)) flowData = response.data.flow;
        else if (response.data.data && Array.isArray(response.data.data.flow)) flowData = response.data.data.flow;
        else if (Array.isArray(response.data.data)) flowData = response.data.data;
        else if (Array.isArray(response.data)) flowData = response.data;
        else {
          for (const key of Object.keys(response.data)) {
            if (Array.isArray(response.data[key])) { flowData = response.data[key]; break; }
          }
        }
      }
      setCampaignFlows(flowData);
    } catch (error) {
      toastError(error);
      setCampaignFlows([]);
    } finally {
      setLoading(false);
    }
  };

  const renderConnections = (whatsappIds) => {
    if (!whatsappIds || !Array.isArray(whatsappIds) || whatsappIds.length === 0) {
      return <Typography variant="caption" color="textSecondary">Sin conexión</Typography>;
    }
    const visible = whatsappIds.slice(0, 2);
    const extra = whatsappIds.length - 2;
    return (
      <Box className={classes.connectionContainer}>
        {visible.map((id) => {
          const wa = whatsApps.find((w) => w.id === id);
          return (
            <Tooltip key={id} title={wa ? `${wa.name} (${wa.status})` : `ID: ${id}`}>
              <Chip
                label={wa ? wa.name : `ID: ${id}`}
                className={classes.whatsappChip}
                size="small"
                color={wa?.status === "CONNECTED" ? "success" : "default"}
                variant="outlined"
              />
            </Tooltip>
          );
        })}
        {extra > 0 && (
          <Chip label={`+${extra}`} className={classes.whatsappChip} size="small" color="primary" variant="outlined" />
        )}
      </Box>
    );
  };

  useEffect(() => {
    getCampaigns();
    getWhatsApps();
  }, []);

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingContact && `¿Eliminar campaña "${deletingContact.name}"?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingContact.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <CampaignModalPhrase
        open={ModalOpenPhrase}
        onClose={() => setModalOpenPhrase(false)}
        FlowCampaignId={campaignflowSelected}
        onSave={getCampaigns}
      />

      <MainHeader>
        <Title>Campañas de Flujo</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => { setCampaignFlowSelected(undefined); setModalOpenPhrase(true); }}
            style={{ textTransform: "none", borderRadius: 10 }}
            startIcon={<AddCircle />}
          >
            {isMobile ? "Nueva" : "Nueva Campaña"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Table header */}
        <Grid container className={classes.tableHeader}>
          <Grid item xs={isMobile ? 7 : 3} style={{ color: colorTopTable(), fontWeight: 600, fontSize: 13 }}>
            Nombre
          </Grid>
          {!isMobile && (
            <Grid item xs={3} style={{ color: colorTopTable(), fontWeight: 600, fontSize: 13 }} align="center">
              Conexiones
            </Grid>
          )}
          <Grid item xs={isMobile ? 3 : 3} style={{ color: colorTopTable(), fontWeight: 600, fontSize: 13 }} align="center">
            Estado
          </Grid>
          <Grid item xs={2} style={{ color: colorTopTable(), fontWeight: 600, fontSize: 13 }} align="right">
            {i18n.t("contacts.table.actions")}
          </Grid>
        </Grid>

        {loading ? (
          <Stack justifyContent="center" alignItems="center" minHeight="30vh">
            <CircularProgress />
          </Stack>
        ) : campaignflows.length > 0 ? (
          campaignflows.map((flow) => (
            <Grid
              container
              key={flow.id}
              alignItems="center"
              sx={{
                padding: "10px 12px",
                backgroundColor: colorLineTable(),
                borderRadius: 2,
                marginTop: 0.5,
                "&:hover": { backgroundColor: colorLineTableHover() },
              }}
            >
              {/* Nombre */}
              <Grid item xs={isMobile ? 7 : 3}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <TextFields style={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600, color: "#ededed" }}>
                      {flow.name || "Sin nombre"}
                    </Typography>
                    {flow.phrase && Array.isArray(flow.phrase) && (
                      <Typography variant="caption" style={{ color: "#94a3b8" }}>
                        {flow.phrase.length} frase{flow.phrase.length !== 1 ? "s" : ""}
                      </Typography>
                    )}
                    {/* Conexiones debajo del nombre en móvil */}
                    {isMobile && (
                      <Box mt={0.5}>{renderConnections(flow.whatsappIds)}</Box>
                    )}
                  </Box>
                </Stack>
              </Grid>

              {/* Conexiones (solo desktop) */}
              {!isMobile && (
                <Grid item xs={3} align="center">
                  {renderConnections(flow.whatsappIds)}
                </Grid>
              )}

              {/* Estado */}
              <Grid item xs={isMobile ? 3 : 3} align="center">
                <Chip
                  label={flow.status ? "Activo" : "Inactivo"}
                  size="small"
                  color={flow.status ? "success" : "default"}
                  variant={flow.status ? "filled" : "outlined"}
                />
              </Grid>

              {/* Acciones */}
              <Grid item xs={2} align="right">
                <Stack direction="row" justifyContent="flex-end">
                  <Tooltip title="Editar campaña">
                    <IconButton
                      size="small"
                      onClick={() => { setCampaignFlowSelected(flow.id); setModalOpenPhrase(true); }}
                    >
                      <EditIcon style={{ color: "#ededed", fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Can
                    role={user.profile}
                    perform="contacts-page:deleteContact"
                    yes={() => (
                      <Tooltip title="Eliminar campaña">
                        <IconButton
                          size="small"
                          onClick={() => { setConfirmModalOpen(true); setDeletingContact(flow); }}
                        >
                          <DeleteOutlineIcon style={{ color: "#ededed", fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
          ))
        ) : (
          <div className={classes.emptyState}>
            <Typography variant="h6" style={{ color: "#64748b" }}>
              Sin campañas aún
            </Typography>
            <Typography variant="body2" style={{ color: "#94a3b8", textAlign: "center" }}>
              Haz clic en "Nueva Campaña" para crear tu primera campaña de flujo
            </Typography>
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default CampaignsPhrase;
